import { useTranslation } from 'react-i18next'
import type { AuditReport } from '@/lib/api/amazon-listing'

interface ConversionDriverTableProps {
  drivers: AuditReport['conversion_drivers']
}

const JOURNEY_COLORS: Record<string, string> = {
  awareness: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  consideration: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
  decision: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  post_purchase: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
}

export function ConversionDriverTable({ drivers }: ConversionDriverTableProps) {
  const { t } = useTranslation()
  if (!drivers?.length) return null

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
      <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-700">
        <h3 className="font-semibold text-neutral-900 dark:text-white">{t('amazonListing.report.conversionDrivers')}</h3>
      </div>
      <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
        {drivers.map((driver, i) => (
          <div key={i} className="px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-1 mb-1">
              <span className="text-sm font-medium text-neutral-900 dark:text-white">{driver.feature}</span>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, j) => (
                    <div key={j} className={`w-2 h-2 rounded-full ${j < driver.relevance ? 'bg-orange-500' : 'bg-neutral-200 dark:bg-neutral-600'}`} />
                  ))}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${JOURNEY_COLORS[driver.journey_phase] || JOURNEY_COLORS.consideration}`}>
                  {t(`amazonListing.report.journey${driver.journey_phase.charAt(0).toUpperCase()}${driver.journey_phase.slice(1).replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())}`, driver.journey_phase)}
                </span>
              </div>
            </div>
            {driver.description && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{driver.description}</p>}
            {driver.data_points?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {driver.data_points.map((dp, k) => (
                  <span key={k} className="px-1.5 py-0.5 rounded text-xs bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">{dp}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
